import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Logo } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEV = { critical: "#F0736E", high: "#F0A85C", medium: "#E7D46A" };

export default function Scan() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("paste");
  const [form, setForm] = useState({ name: "", email: "", company: "", role: "", code: "", repo_url: "", lang: "auto" });
  const [consent, setConsent] = useState(false);
  const [file, setFile] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    setErr("");
    const fd = new FormData();
    fd.append("name", form.name.trim());
    fd.append("email", form.email.trim());
    fd.append("company", form.company.trim());
    fd.append("role", form.role.trim());
    fd.append("consent", consent ? "on" : "");
    if (mode === "paste") { fd.append("code", form.code); fd.append("lang", form.lang); }
    else if (mode === "zip") { if (file) fd.append("file", file); }
    else fd.append("repo_url", form.repo_url.trim());

    setBusy(true);
    try {
      const r = await axios.post(`${API}/scan`, fd);
      setRes(r.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setErr(e.response?.data?.error || "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="wrap" style={{ maxWidth: 820, paddingTop: 28, paddingBottom: 72 }}>
      <div className="brand" style={{ display: "flex", alignItems: "center", gap: 11 }}>
        <Logo w={34} h={28} />
        <a href="/" className="name" style={{ textDecoration: "none" }} onClick={(e) => { e.preventDefault(); navigate("/"); }}>
          veri<span className="g">Auth</span><span className="d">.ai</span></a>
        <div style={{ marginLeft: "auto", fontSize: 12, color: "var(--dim)", textAlign: "right" }}>
          <a href="/" style={{ color: "var(--dim)" }} data-testid="scan-back" onClick={(e) => { e.preventDefault(); navigate("/"); }}>← Back to site</a><br />
          AI Decision Compliance Scan
        </div>
      </div>

      {!res ? (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}>
          <div className="eyebrow" style={{ marginTop: 24 }}>VeriScan · code scan</div>
          <h1 style={{ fontSize: "clamp(26px,4vw,34px)", color: "var(--head)", margin: "12px 0 8px", letterSpacing: "-1px" }}>
            Scan your AI pipeline for unprovable decisions.</h1>
          <p className="lede">Point VeriScan at your code — paste it, upload a <code>.zip</code>, or give a public Git repo or
            file URL. It scans <b>any language</b> (Python, JS/TS, Java, Go, C#, Ruby, PHP, C/C++, and more), flags every place a
            decision or outcome runs without attestation, maps each to regulation and penalty exposure, and returns a preview here.
            Your full report goes to our team, who will reach out.</p>

          <div className="note"><b>Your code is scanned in memory and never stored.</b> Nothing is written to disk. The
            detection engine runs on our server; only a summary preview returns to this page.</div>

          <div className="card">
            <div className="two">
              <div><label htmlFor="name">Your name *</label><input id="name" data-testid="scan-name" value={form.name} onChange={set("name")} /></div>
              <div><label htmlFor="email">Work email *</label><input id="email" data-testid="scan-email" type="email" placeholder="you@yourcompany.com" value={form.email} onChange={set("email")} /></div>
            </div>
            <div className="two">
              <div><label htmlFor="company">Company</label><input id="company" data-testid="scan-company" value={form.company} onChange={set("company")} /></div>
              <div><label htmlFor="role">Role</label><input id="role" data-testid="scan-role" value={form.role} onChange={set("role")} /></div>
            </div>

            <label>How would you like to submit code?</label>
            <div className="tabs" role="group" aria-label="Input method">
              {[["paste", "Paste code"], ["zip", "Upload .zip"], ["repo", "Git / URL"]].map(([m, l]) => (
                <button key={m} type="button" aria-pressed={mode === m} data-testid={`scan-mode-${m}`} onClick={() => setMode(m)}>{l}</button>
              ))}
            </div>

            {mode === "paste" && (
              <div>
                <label htmlFor="code">Paste a file or snippet</label>
                <textarea id="code" data-testid="scan-code" placeholder="// paste source in any language — auto-detect handles the rest" value={form.code} onChange={set("code")} />
                <label htmlFor="lang">Language (optional — leave on auto if unsure)</label>
                <select id="lang" data-testid="scan-lang" value={form.lang} onChange={set("lang")}
                  style={{ width: "100%", background: "var(--ink)", border: "1px solid var(--line2)", borderRadius: 10, color: "var(--head)", padding: "12px 13px", fontSize: 14, fontFamily: "var(--sans)" }}>
                  {[["auto", "Auto-detect"], ["python", "Python"], ["javascript", "JavaScript"], ["typescript", "TypeScript"],
                    ["java", "Java"], ["kotlin", "Kotlin"], ["scala", "Scala"], ["go", "Go"], ["csharp", "C#"],
                    ["c", "C"], ["cpp", "C++"], ["ruby", "Ruby"], ["php", "PHP"], ["rust", "Rust"], ["swift", "Swift"],
                    ["r", "R"], ["sql", "SQL"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            )}
            {mode === "zip" && (
              <div><label htmlFor="file">Upload a .zip of your source (or a single source file, any language)</label>
                <input id="file" data-testid="scan-file" type="file"
                  accept=".zip,.py,.pyi,.js,.jsx,.mjs,.cjs,.ts,.tsx,.java,.kt,.kts,.scala,.go,.cs,.c,.h,.cpp,.cc,.cxx,.hpp,.rb,.php,.rs,.swift,.m,.mm,.r,.jl,.sql,.sh,.ipynb,.yaml,.yml,.tf"
                  onChange={(e) => setFile(e.target.files[0])} /></div>
            )}
            {mode === "repo" && (
              <div><label htmlFor="repo_url">Public Git repository, or a link to a .zip / source file</label>
                <input id="repo_url" data-testid="scan-repo" type="url" placeholder="https://github.com/your-org/your-repo" value={form.repo_url} onChange={set("repo_url")} />
                <div className="note" style={{ marginTop: 10 }}>We shallow-clone or fetch the source over HTTPS, scan every source
                  file <b>in memory</b> across languages, and discard it. Public sources only — no credentials are sent or stored.</div>
              </div>
            )}

            <label className="consent"><input type="checkbox" data-testid="scan-consent" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
              I authorize a read-only static scan of the code I submit or point to. I understand it is processed in memory,
              not stored, and that findings are indicators, not an audit.</label>

            <div className="err" data-testid="scan-error">{err}</div>
            <div style={{ marginTop: 14 }}>
              <button className="btn" id="go" data-testid="scan-submit" disabled={busy} onClick={submit}>
                {busy ? <><span className="spin" /> {mode === "repo" ? "Fetching & scanning…" : "Scanning…"}</> : "Scan my code →"}
              </button>
            </div>
          </div>
          <div className="foot-note">Static, language-agnostic readiness scan · 20+ languages · findings require human review.
            Sources: RBI FREE-AI (advisory); DPDP Act 2023 s.33 (statutory maxima, discretionary). Not legal advice.</div>
        </motion.div>
      ) : (
        <ScanResults d={res} onReset={() => { setRes(null); setForm({ name: "", email: "", company: "", role: "", code: "", repo_url: "", lang: "auto" }); setConsent(false); setFile(null); }} />
      )}
    </div>
  );
}

const Stat = ({ n, l, c }) => (
  <div className="stat"><div className="n" style={c ? { color: c } : {}}>{n}</div><div className="l">{l}</div></div>
);

const ScanResults = ({ d, onReset }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} data-testid="scan-results">
    <div className="eyebrow" style={{ marginTop: 24 }}>Your readiness preview</div>
    <h1 style={{ fontSize: 24, color: "var(--head)", margin: "12px 0 8px" }}>Preview · sample of findings</h1>
    <div className="note">
      <b>Full report {d.delivery === "sent" ? "sent to the VeriAuth team" : "queued for the VeriAuth team"}.</b> We'll reach out at {d.lead_email}.
    </div>
    <div className="stats">
      <Stat n={d.files} l="Files" /><Stat n={d.total} l="Findings" />
      <Stat n={d.critical} l="Critical" c="#F0736E" /><Stat n={d.high} l="High" c="#F0A85C" />
      <Stat n={d.addressable} l="Addressable" c="#83EE98" />
    </div>
    <div className="exp">
      <div className="k">Statutory exposure implicated</div>
      <div className="v">{d.exposure}</div>
      <div className="fine">DPDP penalties are Schedule-capped maximums under s.33 — per violation, discretionary, not automatic. RBI FREE-AI is advisory. Not legal advice.</div>
    </div>
    {d.languages && d.languages.length > 0 && (
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", margin: "14px 0 0" }} data-testid="scan-languages">
        <span className="kv">Languages detected:</span>
        {d.languages.map((L) => <span key={L} className="vtag live">{L}</span>)}
      </div>
    )}
    <h1 style={{ fontSize: 16, marginTop: 22, color: "var(--head)" }}>Sample findings</h1>
    {d.shown.length ? d.shown.map((f) => (
      <div className="find" key={f.id} style={{ borderLeftColor: SEV[f.sev] }}>
        <div className="top"><div className="title">{f.title}</div><div className="sev" style={{ color: SEV[f.sev] }}>{f.sev}</div></div>
        <div className="desc">{f.desc}</div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
          <div className="mod" style={{ margin: 0 }}>{f.module}</div>
          {f.lang && f.lang !== "other" && f.lang !== "unknown" && <span className="vtag">{f.lang}</span>}
        </div>
      </div>
    )) : <div className="note">No sample findings to show for this input.</div>}
    {d.locked_count > 0 && (
      <>
        <div style={{ fontSize: 14, color: "var(--amber)", margin: "18px 0 4px", fontFamily: "var(--mono)", letterSpacing: 1, textTransform: "uppercase" }}>
          Locked · {d.locked_count} finding{d.locked_count > 1 ? "s" : ""}{d.critical ? ` incl. ${d.critical} critical` : ""}
        </div>
        {d.locked_severities.map((sev, i) => (
          <div className="find locked" key={i} style={{ borderLeftColor: SEV[sev] || SEV.high }}>
            <div className="top"><div className="title blur">Finding withheld in preview</div><div className="sev" style={{ color: SEV[sev] || SEV.high }}>{sev}</div></div>
            <div className="desc blur">The file, line, probable attack and fix for this finding are in your full report.</div>
            <div className="lockmask"><div style={{ textAlign: "center" }}><div style={{ fontSize: 20 }}>🔒</div><div className="t">IN FULL REPORT</div></div></div>
          </div>
        ))}
      </>
    )}
    <div className="cta">
      <div className="h">Your full report is on its way to the VeriAuth team.</div>
      <div className="s">The full report unblurs every finding with its file, line, probable attack, penalty exposure and fix — and a 90-day POC that closes them on your pipeline.</div>
    </div>
    <div style={{ marginTop: 16 }}><button className="btn" data-testid="scan-reset" onClick={onReset}>Run another scan</button></div>
  </motion.div>
);
